// Microphone toggle for in-chat dictation. Coordinates the four moving
// pieces required to deliver "text appears as the user speaks":
//
//   1. `getUserMedia` for the input audio track.
//   2. An `AudioContext` + `AudioWorkletNode` running the PCM16
//      downsampler from `public/audio-worklets/pcm16-downsampler.js`.
//      The worklet emits 24 kHz int16 frames; the main thread base64-
//      encodes them and forwards them to the WebSocket.
//   3. `connectDictation` (lib/dictation.ts) for the relay, which
//      bridges to OpenAI Realtime via the backend and surfaces `delta`
//      + `completed` events.
//   4. `useVad` (Silero ONNX, lazy-loaded) for the 5 s silence auto-
//      shutoff.
//
// Visible states: `idle` (mic icon), `loading` (spinner — VAD model
// fetching on first click), `listening` (live audio waveform + elapsed
// timer + stop button, rendered by `WaveformVisualizer`), `error`
// (tooltip explains why).

import { Loader2, Mic, MicOff, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type DictationClient, type DictationEvent, connectDictation } from "../../lib/dictation";
import { Button } from "../ui/button";
import { WaveformVisualizer } from "./WaveformVisualizer";
import { useVad } from "./useVad";

const SILENCE_MS = 5000;
const FINAL_WAIT_MS = 5000;

export type DictationButtonState = "idle" | "loading" | "listening" | "error";

const ARIA_LABELS: Record<DictationButtonState, string> = {
  idle: "Iniciar dictado",
  loading: "Cargando dictado",
  listening: "Detener dictado",
  error: "Dictado no disponible",
};

// Streaming ASR follows an interim/final pattern: deltas are tentative
// (low latency, may have wrong punctuation / context-dependent words),
// the `completed` event carries the canonical transcript of the same
// audio after the model re-evaluates it with the full turn context.
// The button forwards both, and the consumer is expected to:
//   - `turn-start`: insert a separator and remember where this turn
//     begins in the input buffer.
//   - `turn-append`: concatenate the delta verbatim to the buffer.
//   - `turn-finalize`: replace the text from the turn-start index with
//     the canonical transcript (fixes punctuation, capitalization, and
//     context-dependent words like "habrá" → "habrán").
export type TranscriptEvent = "turn-start" | "turn-append" | "turn-finalize";

interface DictationButtonProps {
  onTranscript: (text: string, event: TranscriptEvent) => void;
  disabled?: boolean;
}

export function DictationButton({ onTranscript, disabled }: DictationButtonProps) {
  const [state, setState] = useState<DictationButtonState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  // Owned by `start()` and torn down by `stop()`. Refs survive React
  // re-renders without retriggering effects. The stream + audioContext
  // also live in refs (mirrored to state for the useVad hook to react
  // to) so the unmount cleanup can reach them without a dep array — see
  // the cleanup useEffect below for the reason.
  const clientRef = useRef<DictationClient | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const finalTimerRef = useRef<number | null>(null);
  const lastFinalRef = useRef<string | null>(null);
  // Tracks whether the current turn already emitted delta(s) to the parent.
  // We stream deltas as they arrive (perceived-latency win — the input
  // shows text the moment OpenAI emits tokens, instead of waiting for
  // the full turn `completed` event). On `completed` we only fire the
  // parent callback if no delta was seen — defensive against the rare
  // case where the upstream emits only completed without intermediate
  // deltas, so the user doesn't get a silent turn.
  const turnHadDeltasRef = useRef(false);
  // Keep the latest onTranscript in a ref so the WebSocket handler can
  // call it without forcing the start callback to take it as a dep.
  const startAbortRef = useRef<AbortController | null>(null);
  const startGenerationRef = useRef(0);
  const onTranscriptRef = useRef(onTranscript);
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  const stop = useCallback(() => {
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    // Drop back to idle right away so the mic icon visibly turns off
    // the moment the user clicks. The terminal `completed` event can
    // still land afterwards — it updates the input via the ref-based
    // onTranscript callback, which doesn't read the button state. The
    // FINAL_WAIT_MS timer below is now just a belt-and-suspenders net
    // for the (rare) case where the close event arrives but somehow
    // leaves state stuck on "listening".
    setState((current) => (current === "listening" || current === "loading" ? "idle" : current));
    const client = clientRef.current;
    const source = sourceNodeRef.current;
    const worklet = workletNodeRef.current;
    clientRef.current = null;
    sourceNodeRef.current = null;
    workletNodeRef.current = null;
    if (worklet) {
      worklet.port.onmessage = null;
      worklet.disconnect();
    }
    if (source) {
      source.disconnect();
    }
    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }
    streamRef.current = null;
    setStream(null);
    if (audioContext) {
      void audioContext.close();
    }
    audioContextRef.current = null;
    setAudioContext(null);
    if (client) {
      client.close();
    }
    // Wait for the terminal `completed` event for up to FINAL_WAIT_MS;
    // if none arrives, revert to idle without contaminating the input.
    if (finalTimerRef.current !== null) {
      window.clearTimeout(finalTimerRef.current);
    }
    finalTimerRef.current = window.setTimeout(() => {
      finalTimerRef.current = null;
      setState((current) => (current === "listening" ? "idle" : current));
    }, FINAL_WAIT_MS);
  }, [stream, audioContext]);

  const handleClientEvent = useCallback((event: DictationEvent) => {
    if (event.kind === "delta" && event.text.length > 0) {
      // Stream the token incrementally — the consumer appends each delta
      // as it arrives so the user sees text flowing in (interim result
      // of the streaming ASR). Word boundaries are encoded inside the
      // delta payload (leading space for new words, no leading space for
      // sub-word token fragments), so deltas after the first must be
      // concatenated verbatim — inserting a space between them would
      // produce "hab rán" out of " hab" + "rán".
      const eventKind: TranscriptEvent = turnHadDeltasRef.current ? "turn-append" : "turn-start";
      turnHadDeltasRef.current = true;
      onTranscriptRef.current(event.text, eventKind);
    } else if (event.kind === "completed") {
      if (event.text.trim().length > 0) {
        // Final transcript of the turn: the model re-evaluated the same
        // audio with full-turn context and fixed punctuation, casing,
        // and context-dependent words. If deltas covered the turn we
        // emit `turn-finalize` so the consumer replaces the tentative
        // text with the canonical version (the "Claude-style"
        // correction-as-you-speak UX). When no deltas arrived (rare:
        // very short turn or upstream skipped them) we emit it as
        // `turn-start` so the consumer still gets a non-silent turn.
        const eventKind: TranscriptEvent = turnHadDeltasRef.current
          ? "turn-finalize"
          : "turn-start";
        onTranscriptRef.current(event.text, eventKind);
      }
      lastFinalRef.current = event.text;
      turnHadDeltasRef.current = false;
    } else if (event.kind === "error") {
      setErrorMessage(`No se pudo transcribir (${event.code})`);
      setState("error");
      if (finalTimerRef.current !== null) {
        window.clearTimeout(finalTimerRef.current);
        finalTimerRef.current = null;
      }
    } else if (event.kind === "close") {
      if (finalTimerRef.current !== null) {
        window.clearTimeout(finalTimerRef.current);
        finalTimerRef.current = null;
      }
      setState((current) => (current === "error" ? "error" : "idle"));
    }
  }, []);

  const start = useCallback(async () => {
    startAbortRef.current?.abort();
    const generation = startGenerationRef.current + 1;
    startGenerationRef.current = generation;
    const abort = new AbortController();
    startAbortRef.current = abort;
    const isCurrent = (): boolean =>
      startGenerationRef.current === generation && !abort.signal.aborted;
    if (finalTimerRef.current !== null) {
      window.clearTimeout(finalTimerRef.current);
      finalTimerRef.current = null;
    }
    setErrorMessage(null);
    setState("loading");

    if (
      typeof window.AudioWorkletNode === "undefined" ||
      typeof navigator.mediaDevices === "undefined"
    ) {
      setErrorMessage("Tu navegador no soporta dictado");
      setState("error");
      return;
    }

    let mediaStream: MediaStream;
    try {
      // Enable the browser's built-in audio DSP filters. With a bare
      // `{ audio: true }` request, some browsers skip these to preserve
      // the raw signal; explicitly opting in gives us cleaner input
      // before the audio leaves the device, complementing the server-
      // side `noise_reduction: near_field` further upstream.
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (_err) {
      if (!isCurrent()) return;
      setErrorMessage("Necesitamos permiso del micrófono");
      setState("error");
      return;
    }
    if (!isCurrent()) {
      for (const track of mediaStream.getTracks()) track.stop();
      return;
    }

    const ctx = new AudioContext({ sampleRate: 24000 });
    try {
      await ctx.audioWorklet.addModule("/audio-worklets/pcm16-downsampler.js");
    } catch (_err) {
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      if (!isCurrent()) return;
      setErrorMessage("No se pudo cargar el procesador de audio");
      setState("error");
      return;
    }
    if (!isCurrent()) {
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      return;
    }

    const source = ctx.createMediaStreamSource(mediaStream);
    const worklet = new AudioWorkletNode(ctx, "pcm16-downsampler");
    source.connect(worklet);
    // We don't route the worklet to the destination — no echo from the
    // microphone back to the user's speakers.

    let client: DictationClient;
    try {
      client = await connectDictation({
        onEvent: (event) => {
          if (startGenerationRef.current !== generation) return;
          handleClientEvent(event);
        },
      });
    } catch (_err) {
      worklet.disconnect();
      source.disconnect();
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      if (!isCurrent()) return;
      setErrorMessage("No se pudo abrir la sesión");
      setState("error");
      return;
    }
    if (!isCurrent()) {
      client.close();
      worklet.disconnect();
      source.disconnect();
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      return;
    }

    worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      client.sendAudio(event.data);
    };

    sourceNodeRef.current = source;
    workletNodeRef.current = worklet;
    clientRef.current = client;
    streamRef.current = mediaStream;
    audioContextRef.current = ctx;
    setAudioContext(ctx);
    setStream(mediaStream);
    lastFinalRef.current = null;
    // Reset the turn flag so the first delta of this new session is
    // emitted as `turn-start` even if the previous session was stopped
    // mid-turn without a `completed` event.
    turnHadDeltasRef.current = false;
    if (!isCurrent()) {
      client.close();
      worklet.disconnect();
      source.disconnect();
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      return;
    }
    setState("listening");
  }, [handleClientEvent]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (state === "loading" || state === "listening") {
      stop();
      return;
    }
    void start();
  }, [disabled, state, stop, start]);

  // VAD: when 5 s of silence are detected, auto-stop. The hook is a no-op
  // while `stream`/`audioContext` are null (idle or error states).
  useVad({
    stream,
    audioContext,
    silenceMs: SILENCE_MS,
    onSilence: stop,
  });

  // Stop the active session as soon as the parent disables the button
  // (the chat composer sets `disabled` while sending) — otherwise the
  // user gets "I clicked enviar but my mic is still on".
  useEffect(() => {
    if (disabled && state === "listening") {
      stop();
    }
  }, [disabled, state, stop]);

  // Elapsed-time counter rendered next to the waveform while listening.
  // Reset to zero whenever the session ends; tick every 200 ms so the
  // "0:06" → "0:07" transition is smooth without being a render hot spot.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (state !== "listening") {
      setElapsedMs(0);
      return;
    }
    const startedAt = performance.now();
    const interval = window.setInterval(() => {
      setElapsedMs(performance.now() - startedAt);
    }, 200);
    return () => window.clearInterval(interval);
  }, [state]);

  // Clean up on unmount so an in-flight session doesn't leak. The
  // dependency array MUST stay empty: any non-empty deps cause the
  // cleanup to re-run on every state change inside `start()`
  // (`setAudioContext` + `setStream`), which closes the still-handshaking
  // WebSocket and surfaces as close code 1006 to the user. Refs are used
  // so we don't need state-derived values in the cleanup.
  useEffect(() => {
    return () => {
      startAbortRef.current?.abort();
      if (clientRef.current) {
        clientRef.current.close();
      }
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
      }
      if (workletNodeRef.current) {
        workletNodeRef.current.port.onmessage = null;
        workletNodeRef.current.disconnect();
      }
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
      }
      if (finalTimerRef.current !== null) {
        window.clearTimeout(finalTimerRef.current);
      }
    };
  }, []);

  // While listening, replace the simple icon button with a compact panel
  // (waveform + timer + stop) so the user sees live audio feedback while
  // they speak — same idea as the Codex dictation UI. `stream` and
  // `audioContext` are guaranteed non-null in this branch because they
  // are set in lockstep with `state === "listening"` from `start()`.
  if (state === "listening" && stream && audioContext) {
    // On narrow screens the panel takes its own row at the end of the
    // composer (`order-last w-full`) so it doesn't squeeze the model /
    // reasoning selectors. On `sm+` it stays inline next to the submit
    // button as a compact pill. `justify-center` centers the canvas
    // when the panel is full-width; on desktop it falls back to the
    // default left alignment via `sm:justify-start`.
    return (
      <div className="order-last flex w-full items-center justify-center gap-3 rounded-md border bg-muted/30 px-2 py-1 text-brand-extra-2-40 sm:order-none sm:w-auto sm:justify-start sm:gap-2">
        <WaveformVisualizer stream={stream} audioContext={audioContext} className="block" />
        <span className="inline-block w-10 text-center text-body-3 tabular-nums text-muted-foreground">
          {formatElapsed(elapsedMs)}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="!h-7 !px-1.5"
          onClick={handleClick}
          aria-label={ARIA_LABELS.listening}
          title={ARIA_LABELS.listening}
        >
          <Square className="h-3.5 w-3.5 fill-current text-brand-extra-2-40" />
        </Button>
      </div>
    );
  }

  const Icon = state === "loading" ? Loader2 : state === "error" ? MicOff : Mic;
  const iconClassName = state === "loading" ? "h-5 w-5 animate-spin" : "h-5 w-5";

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="!px-2"
      onClick={handleClick}
      disabled={disabled}
      aria-label={ARIA_LABELS[state]}
      title={state === "error" ? (errorMessage ?? ARIA_LABELS[state]) : ARIA_LABELS[state]}
    >
      <Icon className={iconClassName} />
    </Button>
  );
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
